import {
  CareerPathEvaluationState,
  CareerPathDto,
  EmployeeLevelEnum,
  LatestContractDtoFragment,
  CareerPathEvaluation,
} from '../../../../types'
import { CareerPathType } from './CareerPathType.enum'
import { ContractChangePayload } from '../types'

const evaluateSummary = (summaryValue: number, summaryReferenceMin: number, summaryReferenceMax: number) => {
  if (summaryValue >= summaryReferenceMin && summaryValue <= summaryReferenceMax) {
    return CareerPathEvaluationState.Inline
  } else {
    return CareerPathEvaluationState.OutOfLine
  }
}

export const mapEmployeeToCareerPath = (
  employeeCareerpathData: CareerPathDto[],
  employeeSalary: number,
  employeeTargetBonus: number,
  employeeLevel?: EmployeeLevelEnum,
): CareerPathEvaluation | undefined => {
  let employeeCareerpath = employeeCareerpathData

  if (!employeeCareerpath || !employeeCareerpath.length) {
    return undefined
  }

  if (employeeLevel && employeeLevel !== undefined) {
    employeeCareerpath = employeeCareerpathData.filter((data) => data.level === employeeLevel)
  }

  const careerPathSalary = employeeCareerpath.find((careerPath) => careerPath.careerPathType === CareerPathType.Salary)
  const careerPathBonus = employeeCareerpath.find((careerPath) => careerPath.careerPathType === CareerPathType.Bonus)

  if (!careerPathSalary || !careerPathBonus) {
    return undefined
  }

  const careerPathSalaryMin = careerPathSalary.min
  const careerPathSalaryMax = careerPathSalary.max ? careerPathSalary.max : Infinity

  const careerPathBonusMin = careerPathBonus.min
  const careerPathBonusMax = careerPathBonus.max ? careerPathBonus.max : Infinity

  const salary: number = employeeSalary
  const targetBonus: number = employeeTargetBonus

  const summaryReferenceMin = careerPathSalaryMin + careerPathBonusMin
  const summaryReferenceMax = careerPathSalaryMax + careerPathBonusMax
  const summaryValue = salary + targetBonus

  let salaryEvaluation: CareerPathEvaluationState = CareerPathEvaluationState.OutOfLine
  let bonusEvaluation: CareerPathEvaluationState = CareerPathEvaluationState.OutOfLine

  if (careerPathSalary && careerPathSalary !== undefined && careerPathSalaryMax !== null && careerPathSalaryMax !== undefined) {
    if (salary >= careerPathSalaryMin && salary <= careerPathSalaryMax) {
      salaryEvaluation = CareerPathEvaluationState.Inline
    } else if (salary <= careerPathSalaryMin || salary >= careerPathSalaryMax) {
      salaryEvaluation = CareerPathEvaluationState.OutOfLine
    }
  } else {
    if (salary >= careerPathSalaryMin) {
      salaryEvaluation = CareerPathEvaluationState.Inline
    } else if (salary <= careerPathSalaryMin) {
      salaryEvaluation = CareerPathEvaluationState.OutOfLine
    }
  }

  if (careerPathBonusMax && careerPathBonusMax !== undefined && careerPathBonusMax !== null && careerPathBonusMax !== undefined) {
    if (targetBonus >= careerPathBonusMin && targetBonus <= careerPathBonusMax) {
      bonusEvaluation = CareerPathEvaluationState.Inline
    } else if (targetBonus <= careerPathBonusMin || targetBonus >= careerPathBonusMax) {
      bonusEvaluation = CareerPathEvaluationState.OutOfLine
    }
  } else {
    if (targetBonus >= careerPathBonusMin) {
      bonusEvaluation = CareerPathEvaluationState.Inline
    } else if (targetBonus <= careerPathBonusMin) {
      bonusEvaluation = CareerPathEvaluationState.OutOfLine
    }
  }

  const summary = evaluateSummary(summaryValue, summaryReferenceMin, summaryReferenceMax)
  return { salaryEvaluation, bonusEvaluation, summary, salary: employeeSalary, targetBonus: employeeTargetBonus }
}

export const createCareerPathEvaluation = (
  change: ContractChangePayload,
  currentContract: LatestContractDtoFragment,
  careerPathData: CareerPathDto[],
): CareerPathEvaluation | undefined => {
  const salary = change.salary !== undefined ? change.salary : currentContract.annualSalary ? currentContract.annualSalary.amount : 0
  const targetBonus = change.bonus !== undefined ? change.bonus : currentContract.targetBonus ? currentContract.targetBonus.amount : 0
  const employeeLevel =
    change.employeeLevel !== undefined
      ? change.employeeLevel
      : currentContract.employeeLevel
      ? currentContract.employeeLevel.level
      : undefined

  return mapEmployeeToCareerPath(careerPathData, salary, targetBonus, employeeLevel)
}

export const filterCareerPathReferencesByLevel = (
  careerPath: CareerPathDto[],
  level: EmployeeLevelEnum,
): { bonus: CareerPathDto; salary: CareerPathDto } | undefined => {
  if (!careerPath || !careerPath.length) {
    return undefined
  }
  const bonusReference = careerPath.find((data) => data.level === level && data.careerPathType === CareerPathType.Bonus)
  const salaryReference = careerPath.find((data) => data.level === level && data.careerPathType === CareerPathType.Salary)

  if (!bonusReference || !salaryReference) return undefined

  return { bonus: bonusReference, salary: salaryReference }
}
